import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  createBook,
  regenerateBookVoiceProfile,
  updateBook,
  type CreateBookPayload,
  type UpdateBookPayload,
} from "./api"

export function useCreateBook() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateBookPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return createBook(context, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] })
      queryClient.invalidateQueries({ queryKey: ["company-users"] })
      queryClient.invalidateQueries({ queryKey: ["user", "me"] })
    },
  })
}

export function useUpdateBook() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ bookId, data }: { bookId: string; data: UpdateBookPayload }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return updateBook(context, bookId, data)
    },
    onSuccess: (updatedBook, { bookId }) => {
      queryClient.setQueryData(["book", bookId], updatedBook)
      queryClient.invalidateQueries({ queryKey: ["book"] })
      // Invalidate the books query to refetch all books
      queryClient.invalidateQueries({ queryKey: ["books"] })
      queryClient.invalidateQueries({ queryKey: ["user", "me"] })
    },
  })
}

export function useRegenerateBookVoiceProfile() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (bookId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return regenerateBookVoiceProfile(context, bookId)
    },
    onSuccess: (_payload, bookId) => {
      queryClient.invalidateQueries({ queryKey: ["book", bookId] })
    },
  })
}
